import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
#handleVariantUpdate = (event) => {
  const source = event.detail.data.html;
  if (!source) return;

  const newMediaGallery = source.querySelector('media-gallery');
  if (!newMediaGallery) return;

  const variant = event.detail.resource;
  const mediaId = String(variant?.featured_media?.id || '');
  const imageSrc = variant?.featured_image?.src?.split('?')[0];

  const slides = [...newMediaGallery.querySelectorAll('slideshow-slide')];
  const imageIndex = slides.findIndex((slide) => {
    const media = slide.querySelector('[data-media-id]');
    const image = slide.querySelector('.product-media__image');

    return (
      (mediaId && media?.dataset.mediaId === mediaId) ||
      (imageSrc && image?.src.split('?')[0] === imageSrc)
    );
  });

  this.replaceWith(newMediaGallery);

  if (imageIndex !== -1) {
    requestAnimationFrame(() => {
      newMediaGallery.slideshow?.select(imageIndex, undefined, { animate: false });
    });
  }
};

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
